import { ThresholdMonitor } from './ThresholdMonitor';
import { RangeMonitor } from './RangeMonitor';
import { StatusMonitor } from './StatusMonitor';
import { AtonHostDetails, MonitorService } from '../../types';
import React, { useCallback } from 'react';
import { useLengthUnits } from '../../../Units/useLengthUnits';
import { convertToMeters, isLengthUnit } from '../../../Units/utils';

interface MonitorItemProps {
  monitor: MonitorService;
}

const MonitorItem = ({ monitor }: MonitorItemProps) => {
  const { convertLength } = useLengthUnits();

  // Check if this monitor has a length unit and convert values if needed
  const hasLengthUnit = isLengthUnit(monitor.unit);

  const getConvertedValues = useCallback(() => {
    // Guard clause: return early if no conversion needed
    if (!hasLengthUnit || !monitor.unit) {
      return {
        currentValue: typeof monitor.value === 'number' ? monitor.value : 0,
        alarmThreshold: monitor.alarmThreshold,
        warningThreshold: monitor.warningThreshold,
        alarmRange: monitor.alarmRange,
        warningRange: monitor.warningRange,
        displayUnit: monitor.unit,
      };
    }

    // Convert all values through meters to user's preferred unit
    const currentValue = typeof monitor.value === 'number' ? monitor.value : 0;
    const currentInMeters = convertToMeters(currentValue, monitor.unit);
    const convertedCurrent = convertLength(currentInMeters);

    let convertedAlarmThreshold = monitor.alarmThreshold;
    let convertedWarningThreshold = monitor.warningThreshold;
    let convertedAlarmRange = monitor.alarmRange;
    let convertedWarningRange = monitor.warningRange;

    // Convert threshold values if they exist
    if (monitor.alarmThreshold !== undefined) {
      const alarmInMeters = convertToMeters(
        monitor.alarmThreshold,
        monitor.unit
      );
      convertedAlarmThreshold = convertLength(alarmInMeters).value;
    }

    if (monitor.warningThreshold !== undefined) {
      const warningInMeters = convertToMeters(
        monitor.warningThreshold,
        monitor.unit
      );
      convertedWarningThreshold = convertLength(warningInMeters).value;
    }

    // Convert range values if they exist
    if (monitor.alarmRange) {
      const alarmMinInMeters = convertToMeters(
        monitor.alarmRange.min,
        monitor.unit
      );
      const alarmMaxInMeters = convertToMeters(
        monitor.alarmRange.max,
        monitor.unit
      );
      convertedAlarmRange = {
        min: convertLength(alarmMinInMeters).value,
        max: convertLength(alarmMaxInMeters).value,
      };
    }

    if (monitor.warningRange) {
      const warningMinInMeters = convertToMeters(
        monitor.warningRange.min,
        monitor.unit
      );
      const warningMaxInMeters = convertToMeters(
        monitor.warningRange.max,
        monitor.unit
      );
      convertedWarningRange = {
        min: convertLength(warningMinInMeters).value,
        max: convertLength(warningMaxInMeters).value,
      };
    }

    return {
      currentValue: convertedCurrent.value,
      alarmThreshold: convertedAlarmThreshold,
      warningThreshold: convertedWarningThreshold,
      alarmRange: convertedAlarmRange,
      warningRange: convertedWarningRange,
      displayUnit: convertedCurrent.abbreviation,
    };
  }, [hasLengthUnit, monitor, convertLength]);

  const {
    currentValue,
    alarmThreshold,
    warningThreshold,
    alarmRange,
    warningRange,
    displayUnit,
  } = getConvertedValues();

  if (monitor.type === 'status') {
    return (
      <StatusMonitor
        name={monitor.name}
        description={monitor.description}
        statuses={monitor.statuses || []}
        currentValue={monitor.value}
      />
    );
  }

  if (monitor.type === 'threshold') {
    return (
      <ThresholdMonitor
        name={monitor.name}
        description={monitor.description}
        alarmThreshold={alarmThreshold}
        warningThreshold={warningThreshold}
        currentValue={currentValue}
        unit={displayUnit}
      />
    );
  }

  if (monitor.type === 'range') {
    return (
      <RangeMonitor
        name={monitor.name}
        description={monitor.description}
        alarmRange={alarmRange}
        warningRange={warningRange}
        currentValue={currentValue}
        unit={displayUnit}
      />
    );
  }

  return null;
};
interface AtonMonitorsProps {
  aton: AtonHostDetails;
}

export function AtonMonitors({ aton }: AtonMonitorsProps) {
  if (!aton.monitors?.length) {
    return <div>No monitor data available</div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-4">
        {aton.monitors.map((monitor) => (
          <MonitorItem key={monitor.id} monitor={monitor} />
        ))}
      </div>
    </div>
  );
}
